import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react'
import classNames from 'classnames'
import Router from 'next/router'
import PubSub from 'pubsub-js'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import config, { IConfig, IChild } from '../../config'
import { isNewVersion, VERSION_COOKIE_KEY } from 'lib/version'
import { logger } from 'lib/slardar-utils'
import { GlobalStoreContext } from 'Components/store'
import { LoginStoreContext } from 'New/login/login-store'

export const getLink = (
  link: string | undefined,
  newVersionLink: string | undefined
) => {
  // 必须每次点击的时候实时判断
  if (isNewVersion() && newVersionLink) {
    return newVersionLink
  }

  return link || ''
}

export const childClickHandler = (
  child: IChild,
  userId: string,
  openLogin: (params: any) => void
) => {
  let { as } = child
  // 登录判断
  if (child?.isNeedLogin) {
    if (!userId) {
      openLogin({
        enter_method: child?.loginMethod || 'other',
      })
      return
    }

    // 已经登录需要替换as中的${userId}
    as = as?.replace('{userId}', userId)
  }

  // eslint-disable-next-line no-unused-expressions
  child.onClick?.()
  if (getLink(child?.link, child.newVersionLink) && as) {
    Router.push(getLink(child?.link, child.newVersionLink), as)
  }
}

export default function ChildWrap() {
  const { state: globalStore } = useContext(GlobalStoreContext)
  const { userData } = globalStore
  const { openLogin } = useContext(LoginStoreContext)

  const [url, setUrl] = useState('')

  const routeChangeComplete = useCallback((url: string) => {
    if (url === '') {
      setUrl('/')
      return
    }
    setUrl(url)
  }, [])

  const isActive = useCallback(
    (as: string | undefined, isActive, isNeedLogin = false) => {
      // url带有VERSION_COOKIE_KEY的参数时，返回有bug
      if (url?.indexOf(VERSION_COOKIE_KEY) > -1 && as === '/') {
        return true
      }
      if (url === as && !isNeedLogin) {
        return true
      }

      if (isActive && isActive(url, userData?.user_id)) {
        return true
      }
      return false
    },
    [url]
  )

  useEffect(() => {
    setUrl(window?.location?.pathname || '/')
    Router.events.on('routeChangeComplete', routeChangeComplete)
    return () => {
      Router.events.off('routeChangeComplete', routeChangeComplete)
    }
  }, [])

  const [fixType, setFixType] = useState('')
  const [handleType, setHandleType] = useState(fixType)
  const handler = useCallback((type, value) => {
    if (fixType) {
      return
    }
    setHandleType(value)
  }, [])

  const [show, setShow] = useState(false)
  const [id, setId] = useState('')
  const [wrapTop, setWrapTop] = useState(0)
  const showHandler = useCallback((type, params) => {
    setShow(params?.status)
    setId(params?.id)
    if (params?.id) {
      try {
        const navItemDOM = document.querySelector(
          `#sideItemWrap_${params?.id}`
        ) as HTMLElement
        const rect = navItemDOM?.getBoundingClientRect?.()
        setWrapTop(rect?.top - 84)
      } catch (err) {
        logger.warn(
          `[side item height] Error get side item height, err: ${err?.message}`
        )
      }
    }
  }, [])

  const fixHandler = useCallback((type, value) => {
    if (value) {
      setFixType('on')
      return
    }
    setFixType('')
  }, [])

  useEffect(() => {
    const token = PubSub.subscribe('SIDE_NAV_STATUS', handler)
    const showToken = PubSub.subscribe('HOVER_NAV_CHILD', showHandler)
    const fixToken = PubSub.subscribe('OPEN_FIX_NAV', fixHandler)
    return () => {
      PubSub.unsubscribe(token)
      PubSub.unsubscribe(showToken)
      PubSub.unsubscribe(fixToken)
    }
  }, [])

  const getClassName: string = useMemo(() => {
    if (fixType) {
      return fixType
    }
    if (handleType) {
      return handleType
    }
    return 'tw-left-88 lg:tw-left-160'
  }, [handleType, fixType, show])

  const childClick = useCallback(
    (child: IChild) => {
      childClickHandler(child, userData?.user_id || '', openLogin)
    },
    [userData?.user_id, openLogin]
  )

  return (
    <>
      <div className="car-modal-mask tw-hidden 2xl:tw-block">
        <style jsx>
          {`
            .car-modal-mask {
              width: calc((100% - 1920px) / 2);
              position: fixed;
              left: 0;
              height: 100%;
              top: 0;
              background: #e6e8f2;
              z-index: 999;
            }
          `}
        </style>
      </div>
      <div
        className={classNames(getClassName, {
          'side-hover-item': true,
          active: show,
        })}
      >
        {config?.map((item: IConfig) => {
          if (id === item?.id && item?.children?.length) {
            return (
              <ul className="side-child-wrap">
                {item?.children?.map((child: IChild, i: number) => (
                  <li key={`side-child-${i}`}>
                    <a
                      className={classNames('side-item', {
                        active: isActive(
                          child.as,
                          child?.isActive,
                          child?.isNeedLogin
                        ),
                      })}
                      onClick={(e) => {
                        e.stopPropagation()
                        sendTeaCommEvent('clk_event', {
                          event_id: 'common_menu_item',
                          button_name: child.name,
                        })
                        childClick(child)
                      }}
                    >
                      <span className={`icon lg:tw-inline-block tw-hidden`} />
                      <span className="name">{child.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )
          }
          return null
        })}
        <style jsx>
          {`
            .side-child-wrap {
              width: 112px;
              background: #fff;
              border-radius: 2px;
            }
            .side-item {
              display: block;
              height: 54px;
              line-height: 54px;
              font-size: 16px;
              color: #606370;
              text-align: center;
              cursor: pointer;
            }

            .side-item.active {
              font-weight: 500;
              color: #606370;
              background-color: #ffcc32;
            }

            .side-item:hover {
              background: #fff2ce;
              color: #1f2129;
              cursor: pointer;
            }

            .side-hover-item {
              transform: translate3d(-110%, 0, 0);
            }
            .side-hover-item.active {
              transform: translate3d(0, 0, 0);
            }
            .side-hover-item:hover {
              transform: translate3d(0, 0, 0);
            }
            .side-hover-item {
              z-index: 9;
              position: absolute;
              border-top: 8px solid transparent;
              border-left: 8px solid transparent;
              top: ${wrapTop}px;
              transition: transform 0.3s ease-in-out;
            }

            .on.side-hover-item {
              left: 160px !important;
              display: none;
            }

            .off.side-hover-item {
              left: 88px !important;
              display: block;
            }
          `}
        </style>
      </div>
    </>
  )
}
